import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FiArrowLeft, FiEdit2, FiTrash2, FiCalendar, FiUser, 
  FiThumbsUp, FiBookOpen, FiAward, FiShare2
} from 'react-icons/fi';
import lessonLearnedService from '../../services/lessonLearnedService';

const LessonLearnedView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLessonLearned();
  }, [id]);

  const fetchLessonLearned = async () => {
    try {
      setLoading(true);
      const data = await lessonLearnedService.getLessonLearned(id);
      setLesson(data);
    } catch (error) {
      toast.error('Failed to fetch lesson learned');
      navigate('/app/lessons-learned');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this lesson learned?')) {
      return;
    }

    try {
      await lessonLearnedService.deleteLessonLearned(id);
      toast.success('Lesson learned deleted successfully');
      navigate('/app/lessons-learned');
    } catch (error) {
      toast.error('Failed to delete lesson learned');
    }
  };

  const handleMarkHelpful = async () => {
    try {
      await lessonLearnedService.markAsHelpful(id);
      toast.success('Marked as helpful');
      fetchLessonLearned();
    } catch (error) {
      toast.error('Failed to mark as helpful');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading lesson learned...</div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Lesson learned not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/app/lessons-learned')}
            className="text-gray-600 hover:text-gray-900"
          >
            <FiArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{lesson.title}</h1>
            <p className="text-gray-600 mt-1">{lesson.project?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleMarkHelpful}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <FiThumbsUp /> Helpful
          </button>
          <Link
            to={`/app/lessons-learned/${id}/edit`}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <FiEdit2 /> Edit
          </Link>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <FiTrash2 /> Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm text-gray-600">Category</div>
          <div className="text-lg font-bold text-gray-900 mt-1">{lesson.category}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm text-gray-600">Type</div>
          <div className="text-lg font-bold text-gray-900 mt-1">{lesson.type}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm text-gray-600">Impact</div>
          <div className="text-lg font-bold text-gray-900 mt-1">{lesson.impact}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm text-gray-600">Views / Helpful</div>
          <div className="text-lg font-bold text-gray-900 mt-1">
            {lesson.views_count} / {lesson.helpful_count}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Situation</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{lesson.situation}</p>
          </div>

          {lesson.action_taken && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Action Taken</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{lesson.action_taken}</p>
            </div>
          )}

          {lesson.result && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Result</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{lesson.result}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Lesson Learned</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{lesson.lesson}</p>
          </div>

          {lesson.recommendation && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recommendation</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{lesson.recommendation}</p>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-gray-600">Status</div>
                <div className="text-gray-900 font-medium mt-1">{lesson.status}</div>
              </div>
              <div>
                <div className="text-gray-600">Applicability</div>
                <div className="text-gray-900 font-medium mt-1">{lesson.applicability}</div>
              </div>
              {lesson.phase && (
                <div>
                  <div className="text-gray-600">Phase</div>
                  <div className="text-gray-900 font-medium mt-1">{lesson.phase}</div>
                </div>
              )}
              <div>
                <div className="text-gray-600">Date Identified</div>
                <div className="text-gray-900 mt-1 flex items-center gap-2">
                  <FiCalendar className="text-gray-400" />
                  {formatDate(lesson.date_identified)}
                </div>
              </div>
              <div>
                <div className="text-gray-600">Shared</div>
                <div className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                  {lesson.is_shared ? (
                    <>
                      <FiShare2 className="text-green-600" />
                      <span className="text-green-600">Yes</span>
                    </>
                  ) : (
                    <span className="text-gray-600">No</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {lesson.identified_by && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Identified By</h2>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <FiUser className="text-blue-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {lesson.identified_by.first_name} {lesson.identified_by.last_name}
                  </div>
                  <div className="text-sm text-gray-600">{lesson.identified_by.email}</div>
                </div>
              </div>
            </div>
          )}

          {lesson.reviewed_by && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Reviewed By</h2>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <FiAward className="text-green-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {lesson.reviewed_by.first_name} {lesson.reviewed_by.last_name}
                  </div>
                  <div className="text-sm text-gray-600">{formatDate(lesson.review_date)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonLearnedView;

